import { useMemo, useState } from 'react';
import {
  ListChecks, ArrowLeft, ArrowRight, Check, Download, Copy, Info, HelpCircle,
  Plus, Trash2, ChevronDown, ChevronRight, CheckCircle2, FileCode2,
  Upload, X, Wallet, Coins, AlertTriangle,
} from 'lucide-react';
import {
  NODE_DEFS, makeNode, validateNode, nodeFieldVisible,
  buildTreeRoots, buildBatch, parseBatch,
} from '../../data/accNodes';

// Plain-language intro shown above each Sub_Program section step.
const SECTION_INTRO = {
  'Identity & strategy': 'Tell us which program this sub-program belongs to and how it authorizes spending. HAWK adds healthcare (IIAS) eligibility checks; VULTURE does not.',
  'General program info': 'High-level descriptors — what kind of program this is and how card numbers and proxies are handled.',
  'Authorization': 'How transactions get approved — identity verification, immediate credit, expired-purse handling, and delegated authorization.',
  'Card parameters': 'Everyday card behavior — whether cards are active, personalized, reloadable, and the status they start in.',
  'PIN handling': 'Only relevant if your cards use a PIN. Controls how PINs are delivered, seen, changed and locked.',
  'Card expiration': 'How long cards last and how their expiry date is calculated.',
  'Auto renewal': 'Whether cards are automatically reissued before they expire.',
  'Card production': 'Which package (card design), bank and BIN produce the cards, and who fulfills them.',
  'Embossing & fulfillment': 'What gets printed on the card and the mailer — return address, extra lines, and discretionary data.',
  'Servicing & disputes': 'Servicing options such as Reg E, dispute handling and negative-balance management.',
};

// Optional add-ons offered on the "Add-ons" step.
const EXTRAS = [
  { type: 'defaultPurse', attach: 'sub', blurb: 'Where reload-network money lands.' },
  { type: 'highRiskMcc', attach: 'sub', blurb: 'Decline or allow a risky merchant category.' },
  { type: 'fudgeMcc', attach: 'sub', blurb: 'Allow a small over/under spend tolerance on an MCC.' },
  { type: 'packageProgram', attach: 'sub', blurb: 'Link a package with a BIN and card count.' },
  { type: 'pursePrioritySet', attach: 'root', blurb: 'Set the order purses are tried in.' },
  { type: 'configureFee', attach: 'root', blurb: 'Set a card replacement or reissue fee.' },
  { type: 'blockedCountry', attach: 'root', blurb: 'Block or re-allow spending in a country.' },
  { type: 'addressVelocity', attach: 'root', blurb: 'Limit how often an address can change.' },
  { type: 'daSetup', attach: 'root', blurb: 'Direct Access — bank account/routing for ACH loads.' },
  { type: 'spPackageLink', attach: 'root', blurb: 'Attach a package to the sub-program (BIN/PRIN).' },
  { type: 'spPackageBpaLink', attach: 'root', blurb: 'Attach a package with a BIN/PRIN/Agent range.' },
];

function Field({ field, value, error, onChange }) {
  const base = 'w-full rounded-lg border px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 ' +
    (error ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300 focus:border-indigo-400');
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-1">{field.label}</label>
      {field.type === 'select' ? (
        <select className={base} value={value} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => <option key={o} value={o}>{o === '' ? '— none —' : o}</option>)}
        </select>
      ) : (
        <input className={base} type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          step={field.type === 'number' ? 'any' : undefined} placeholder={field.placeholder || ''} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.help && <p className="text-xs text-gray-500 mt-1 flex items-start gap-1"><HelpCircle className="w-3.5 h-3.5 flex-none mt-0.5 text-gray-400" />{field.help}</p>}
      {error && <p className="text-xs text-red-500 mt-1 font-medium">{error}</p>}
    </div>
  );
}

// Render the visible fields of a node (optionally a single section).
function NodeFields({ node, section, onChange, showErrors }) {
  const def = NODE_DEFS[node.type];
  const sections = section ? [section] : def.sections;
  const errs = showErrors ? validateNode(node) : {};
  return (
    <div className="space-y-5">
      {sections.map((s) => {
        const visible = s.fields.filter((f) => nodeFieldVisible(f, node.values));
        if (!visible.length) return null;
        return (
          <div key={s.title} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {visible.map((f) => (
              <Field key={f.id} field={f} value={node.values[f.id] ?? ''} error={errs[f.id]} onChange={(v) => onChange(f.id, v)} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function AccGuidedBuilder() {
  const [businessPartner, setBusinessPartner] = useState('Sunny Benefits');
  const [filename, setFilename] = useState('sunny_subprogram');
  const [sub, setSub] = useState(() => makeNode('sub_program'));
  const [subKids, setSubKids] = useState([]); // purses + sub-nested extras
  const [rootNodes, setRootNodes] = useState([]); // standalone extras
  const [step, setStep] = useState(0);
  const [openCard, setOpenCard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showXml, setShowXml] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');

  const subDef = NODE_DEFS.sub_program;
  const steps = useMemo(() => ([
    { key: 'start', title: 'Get started' },
    ...subDef.sections.map((s, i) => ({ key: `sec${i}`, title: s.title, section: s })),
    { key: 'purses', title: 'Purses' },
    { key: 'extras', title: 'Add-ons' },
    { key: 'review', title: 'Review & export' },
  ]), [subDef]);
  const cur = steps[step];

  const purses = subKids.filter((n) => n.type === 'purse');
  const subForBuild = { ...sub, children: subKids };
  const allRoots = [subForBuild, ...rootNodes];
  const fullXml = useMemo(() => buildBatch(businessPartner, buildTreeRoots(allRoots)), [businessPartner, sub, subKids, rootNodes]); // eslint-disable-line react-hooks/exhaustive-deps
  const safeName = (filename || 'acc_subprogram').replace(/[^A-Za-z0-9_]/g, '_');

  // ---- step validity ----
  const subErrors = validateNode(sub);
  const sectionStepErrors = cur.section
    ? cur.section.fields.filter((f) => nodeFieldVisible(f, sub.values)).reduce((acc, f) => (subErrors[f.id] ? { ...acc, [f.id]: subErrors[f.id] } : acc), {})
    : {};
  const purseErrCount = purses.reduce((n, p) => n + Object.keys(validateNode(p)).length, 0);
  const extraErrCount = [...subKids.filter((n) => n.type !== 'purse'), ...rootNodes].reduce((n, x) => n + Object.keys(validateNode(x)).length, 0);
  const totalErrors = Object.keys(subErrors).length + purseErrCount + extraErrCount;
  const stepValid = cur.section ? Object.keys(sectionStepErrors).length === 0
    : cur.key === 'purses' ? purseErrCount === 0
      : cur.key === 'extras' ? extraErrCount === 0 : true;

  // ---- mutations ----
  const setSubVal = (id, val) => setSub((s) => ({ ...s, values: { ...s.values, [id]: val } }));
  const setSubPa = (code) => setSub((s) => ({ ...s, values: { ...s.values, __pa: code } }));
  const updateKid = (nid, id, val) => setSubKids((list) => list.map((n) => n.id === nid ? { ...n, values: { ...n.values, [id]: val } } : n));
  const updateRoot = (nid, id, val) => setRootNodes((list) => list.map((n) => n.id === nid ? { ...n, values: { ...n.values, [id]: val } } : n));
  const addPurse = () => { const p = makeNode('purse'); setSubKids((l) => [...l, p]); setOpenCard(p.id); };
  const addExtra = (e) => {
    const node = makeNode(e.type);
    if (e.attach === 'sub') setSubKids((l) => [...l, node]); else setRootNodes((l) => [...l, node]);
    setOpenCard(node.id);
  };
  const removeNode = (nid) => { setSubKids((l) => l.filter((n) => n.id !== nid)); setRootNodes((l) => l.filter((n) => n.id !== nid)); };

  function goNext() { if (stepValid) setStep((s) => Math.min(s + 1, steps.length - 1)); }
  function goBack() { setStep((s) => Math.max(s - 1, 0)); }
  function restart() { setSub(makeNode('sub_program')); setSubKids([]); setRootNodes([]); setStep(0); }

  function copyXml() { navigator.clipboard?.writeText(fullXml).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }
  function downloadXml() {
    const blob = new Blob([fullXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `${safeName}.XML`; a.click(); URL.revokeObjectURL(url);
  }
  function doImport() {
    try {
      const { businessPartner: bp, roots } = parseBatch(importText);
      const subIdx = roots.findIndex((r) => r.type === 'sub_program');
      if (subIdx < 0) throw new Error('No <Sub_Program> found in this batch.');
      const s = roots[subIdx];
      setSub({ ...s, children: [] });
      setSubKids(s.children || []);
      setRootNodes(roots.filter((_, i) => i !== subIdx));
      if (bp) setBusinessPartner(bp);
      setImportOpen(false); setImportText(''); setImportError(''); setStep(1);
    } catch (e) { setImportError(e.message || 'Could not parse the batch.'); }
  }

  // card (purse / extra) editor
  function NodeCard({ node, onChange, label, sub: subtitle }) {
    const def = NODE_DEFS[node.type];
    const Icon = def.icon === 'Coins' ? Coins : Wallet;
    const open = openCard === node.id;
    const errs = Object.keys(validateNode(node)).length;
    return (
      <div className="rounded-xl border border-gray-200 bg-white">
        <button onClick={() => setOpenCard(open ? null : node.id)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <Icon className="w-4 h-4 text-indigo-500" />
          <span className="font-medium text-gray-900 text-sm">{label}</span>
          {subtitle && <span className="text-xs text-gray-400 font-mono">{subtitle}</span>}
          {errs > 0 && <span className="text-[10px] font-bold text-red-500 bg-red-50 rounded-full px-1.5">{errs}</span>}
          <span className="ml-auto" />
          <Trash2 className="w-4 h-4 text-gray-300 hover:text-red-500" onClick={(e) => { e.stopPropagation(); removeNode(node.id); }} />
        </button>
        {open && <div className="px-4 pb-4"><NodeFields node={node} onChange={(id, val) => onChange(node.id, id, val)} showErrors /></div>}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-indigo-500" /> Guided Builder
          </h2>
          <p className="text-gray-500 mt-1 text-sm">Build a complete sub-program configuration step by step — we explain each part and assemble the FIS file for you.</p>
        </div>
        <button onClick={() => { setImportOpen(true); setImportError(''); }} className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border border-gray-300 text-gray-700 hover:border-indigo-300 hover:text-indigo-600">
          <Upload className="w-4 h-4" /> Import XML
        </button>
      </div>

      {/* Stepper rail */}
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
          <span className="font-semibold text-gray-700">{cur.title}</span>
          <span>Step {step + 1} of {steps.length}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
        <div className="flex flex-wrap gap-1 mt-2">
          {steps.map((s, i) => (
            <button key={s.key} onClick={() => setStep(i)} title={s.title}
              className={`h-1.5 flex-1 min-w-[10px] rounded-full ${i <= step ? 'bg-indigo-300' : 'bg-gray-100'} hover:bg-indigo-400`} />
          ))}
        </div>
      </div>

      {/* Step body */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-5">
        {/* START */}
        {cur.key === 'start' && (
          <>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Let’s set up a sub-program</h3>
              <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3.5 py-2.5 text-sm text-indigo-900 flex gap-2">
                <Info className="w-4 h-4 flex-none mt-0.5 text-indigo-500" />
                <span>We’ll walk through the sub-program’s settings one section at a time, then its purses, then optional add-ons. Hover the <HelpCircle className="w-3.5 h-3.5 inline -mt-0.5" /> hints on any field. You can jump between steps using the bars above.</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Company / Business Partner</label>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40" value={businessPartner} onChange={(e) => setBusinessPartner(e.target.value)} />
                <p className="text-xs text-gray-500 mt-1">Your organization’s top-level name in FIS.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">File name</label>
                <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
                  <input className="px-3 py-2 text-sm focus:outline-none flex-1" value={filename} onChange={(e) => setFilename(e.target.value)} />
                  <span className="px-2 text-xs text-gray-400 bg-gray-50 border-l border-gray-200 py-2">.XML</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Letters, numbers and underscores only.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Are you creating or changing it?</label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none" value={sub.values.__pa} onChange={(e) => setSubPa(e.target.value)}>
                  {subDef.pa.map((p) => <option key={p.code} value={p.code}>{p.code === '1' ? 'Creating a new sub-program' : 'Changing an existing sub-program'}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">“Changing” only fills in what you edit; “creating” needs the core fields.</p>
              </div>
            </div>
          </>
        )}

        {/* SUB-PROGRAM SECTION */}
        {cur.section && (
          <>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{cur.section.title}</h3>
              {SECTION_INTRO[cur.section.title] && (
                <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3.5 py-2.5 text-sm text-indigo-900 flex gap-2">
                  <Info className="w-4 h-4 flex-none mt-0.5 text-indigo-500" /><span>{SECTION_INTRO[cur.section.title]}</span>
                </div>
              )}
            </div>
            <NodeFields node={sub} section={cur.section} onChange={setSubVal} showErrors />
          </>
        )}

        {/* PURSES */}
        {cur.key === 'purses' && (
          <>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Purses</h3>
              <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3.5 py-2.5 text-sm text-indigo-900 flex gap-2">
                <Info className="w-4 h-4 flex-none mt-0.5 text-indigo-500" />
                <span>Add the pots of money on the card. Each purse has its own dates, allowed categories (MCC group), limits and optional cash tracks. Add as many as you need.</span>
              </div>
            </div>
            <div className="space-y-3">
              {purses.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No purses yet.</p>}
              {purses.map((p, i) => (
                <NodeCard key={p.id} node={p} onChange={updateKid} label={`Purse ${i + 1}`} sub={p.values.PurseName} />
              ))}
              <button onClick={addPurse} className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border border-indigo-200 text-indigo-600 hover:bg-indigo-50">
                <Plus className="w-4 h-4" /> Add a purse
              </button>
            </div>
          </>
        )}

        {/* EXTRAS */}
        {cur.key === 'extras' && (
          <>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Add-ons (optional)</h3>
              <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3.5 py-2.5 text-sm text-indigo-900 flex gap-2">
                <Info className="w-4 h-4 flex-none mt-0.5 text-indigo-500" />
                <span>Attach extra pieces of configuration if you need them. Each one becomes part of the same file. Skip any you don’t need.</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {EXTRAS.map((e) => (
                <button key={e.type} onClick={() => addExtra(e)} className="text-left rounded-lg border border-gray-200 p-3 hover:border-indigo-300 hover:bg-indigo-50/40">
                  <div className="flex items-center gap-1.5 font-medium text-gray-900 text-sm"><Plus className="w-3.5 h-3.5 text-indigo-500" />{NODE_DEFS[e.type].label}</div>
                  <p className="text-xs text-gray-500 mt-0.5">{e.blurb}</p>
                </button>
              ))}
            </div>
            {(subKids.some((n) => n.type !== 'purse') || rootNodes.length > 0) && (
              <div className="space-y-3 pt-2">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Added add-ons</p>
                {subKids.filter((n) => n.type !== 'purse').map((n) => (
                  <NodeCard key={n.id} node={n} onChange={updateKid} label={NODE_DEFS[n.type].label} />
                ))}
                {rootNodes.map((n) => (
                  <NodeCard key={n.id} node={n} onChange={updateRoot} label={NODE_DEFS[n.type].label} />
                ))}
              </div>
            )}
          </>
        )}

        {/* REVIEW */}
        {cur.key === 'review' && (
          <>
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Review &amp; export</h3>
              <p className="text-sm text-gray-500 mt-1">A quick summary of what this file will do. Jump back to any step to adjust.</p>
            </div>
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 text-sm">
              <div className="flex gap-4 px-4 py-2.5"><span className="text-gray-500 w-40 flex-none">Sub-program</span><span className="font-medium text-gray-900">{sub.values.Sub_ProgramName || '—'} {sub.values.__pa === '1' ? '(new)' : '(change)'}</span></div>
              <div className="flex gap-4 px-4 py-2.5"><span className="text-gray-500 w-40 flex-none">Program</span><span className="font-medium text-gray-900">{sub.values.ProgramName || '—'}</span></div>
              <div className="flex gap-4 px-4 py-2.5"><span className="text-gray-500 w-40 flex-none">Strategy</span><span className="font-medium text-gray-900">{sub.values.AuthStrategy || '—'}</span></div>
              <div className="flex gap-4 px-4 py-2.5"><span className="text-gray-500 w-40 flex-none">Purses</span><span className="font-medium text-gray-900">{purses.map((p) => p.values.PurseName).filter(Boolean).join(', ') || 'none'}</span></div>
              <div className="flex gap-4 px-4 py-2.5"><span className="text-gray-500 w-40 flex-none">Add-ons</span><span className="font-medium text-gray-900">{[...subKids.filter((n) => n.type !== 'purse'), ...rootNodes].map((n) => NODE_DEFS[n.type].label).join(', ') || 'none'}</span></div>
            </div>
            {totalErrors > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-sm text-amber-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-none mt-0.5" /><span>{totalErrors} field{totalErrors > 1 ? 's' : ''} still need attention. Use the step bars above to find the red marks.</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={downloadXml} className="inline-flex items-center gap-2 bg-indigo-600 text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-indigo-700"><Download className="w-4 h-4" /> Download the file</button>
              <button onClick={copyXml} className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 rounded-lg px-4 py-2.5 hover:bg-gray-50">{copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy text'}</button>
            </div>
          </>
        )}

        {/* Preview file (collapsible, available on every step) */}
        <div className="rounded-xl border border-gray-200">
          <button onClick={() => setShowXml((s) => !s)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700">
            {showXml ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <FileCode2 className="w-4 h-4" /> Preview the file so far
            <span className="ml-auto text-xs text-gray-400">{new Blob([fullXml]).size} bytes</span>
          </button>
          {showXml && <pre className="text-[12px] leading-relaxed font-mono whitespace-pre overflow-auto p-4 bg-slate-900 text-slate-300 rounded-b-xl max-h-80">{fullXml}</pre>}
        </div>
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between">
        <button onClick={goBack} disabled={step === 0} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={restart} className="text-sm text-gray-400 hover:text-gray-600">Start over</button>
        {step < steps.length - 1 ? (
          <button onClick={goNext} disabled={!stepValid} className="inline-flex items-center gap-1.5 bg-indigo-600 text-white font-semibold rounded-lg px-4 py-2 hover:bg-indigo-700 disabled:opacity-50">Next <ArrowRight className="w-4 h-4" /></button>
        ) : <span className="w-16" />}
      </div>

      {/* Import modal */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setImportOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Upload className="w-4 h-4 text-indigo-500" /> Import an existing file</h3>
              <button onClick={() => setImportOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">Paste a <code className="font-mono">&lt;Batch&gt;</code> file containing a <code className="font-mono">&lt;Sub_Program&gt;</code> to pre-fill every step.</p>
              <textarea value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={'<?xml version="1.0" encoding="utf-8"?>\n<Batch BusinessPartner="...">\n  <Sub_Program>...</Sub_Program>\n</Batch>'} className="w-full h-56 rounded-lg border border-gray-300 p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400/40" />
              {importError && <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {importError}</p>}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50/60 rounded-b-2xl">
              <button onClick={() => setImportOpen(false)} className="text-sm rounded-lg px-3 py-2 border border-gray-300 text-gray-600 hover:bg-white">Cancel</button>
              <button onClick={doImport} disabled={!importText.trim()} className="text-sm font-semibold rounded-lg px-3 py-2 bg-indigo-600 text-white disabled:opacity-40 hover:bg-indigo-700">Import &amp; pre-fill</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
